import { motion } from 'framer-motion';
import Link from 'next/link';
import { useState } from 'react';

const CMCFAQ = () => {
  const [openIndices, setOpenIndices] = useState([]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const toggleAccordion = (index) => {
    if (openIndices.includes(index)) {
      setOpenIndices(openIndices.filter((i) => i !== index)); // Close accordion if it's already open
    } else {
      setOpenIndices([...openIndices, index]); // Open the clicked accordion
    }
  };

  const faqItems = [
    {
      question: 'What is a Common Mark Certificate (CMC)?',
      answer:
        'A Common Mark Certificate (CMC) lets you display your logo in Gmail via BIMI without a trademark—Brandymail makes this accessible solution enhance your email brand presence.',
    },
    {
      question: 'Who Should Consider Using a CMC Instead of a VMC?',
      answer:
        'Startups and small-to-medium businesses without trademarks benefit from CMCs—Brandymail helps you leverage this cost-effective option to display your logo in Gmail.',
    },
    {
      question: 'What Are the Logo Requirements for a CMC?',
      answer:
        'Your logo needs 12 months of consistent use, not a trademark, and must be in SVG format—Brandymail ensures it meets BIMI specs to represent your brand perfectly.',
    },
    {
      question: 'Which Email Providers Support CMC?',
      answer:
        'Gmail primarily supports CMCs for logo display via BIMI—Brandymail ensures your logo appears seamlessly in Gmail to boost your brand’s visibility.',
    },
    {
      question: 'How Long Does It Take to Implement a CMC?',
      answer:
        'CMC implementation takes 1-2 weeks, faster than VMCs, and Brandymail streamlines the process by guiding you through documentation and BIMI record setup.',
    },
    {
      question: 'How Does the Cost of a CMC Compare to a VMC?',
      answer:
        'CMCs are more affordable than VMCs, skipping trademark validation—Brandymail helps you adopt this budget-friendly solution to enhance your email brand presence.',
    },
  ];

  const handleScrollToPricing = (e) => {
    e.preventDefault();
    const pricingElement = document.getElementById('pricing');
    if (pricingElement) {
      const offsetTop = pricingElement.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div>
      <section className="section">
        <div className="container-default w-container">
          <div className="inner-container _700px---tablet center">
            <div className="inner-container _600px---mbl center">
              <motion.div
                className="mg-bottom-80px"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <div className="inner-container _745px center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        Frequently Asked Questions
                      </div>
                    </div>
                    <h2 className="display-2 color-neutral-100">CMC Questions Answered</h2>
                    <p className="color-neutral-300 mg-bottom-0 paragraph-medium">
                      Discover everything you need to know about Common Mark Certificates (CMC) and
                      how they can help businesses without registered trademarks display their logos
                      in Gmail.
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="w-layout-grid grid-2-columns accordions-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {faqItems.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className={`accordion-item-wrapper ${openIndices.includes(index) ? 'open' : ''}`}
                    onClick={() => toggleAccordion(index)}
                    style={{
                      backgroundColor: openIndices.includes(index)
                        ? 'rgb(36, 123, 254)'
                        : 'rgb(40, 41, 43)',
                    }}
                  >
                    <div className="accordion-content-wrapper">
                      <div className="accordion-header">
                        <h3 className="text-400 bold color-neutral-100 mg-bottom-0">
                          {item.question}
                        </h3>
                      </div>
                      <div
                        className="acordion-body"
                        style={{
                          display: openIndices.includes(index) ? 'block' : 'none',
                          opacity: openIndices.includes(index) ? 1 : 0,
                          height: openIndices.includes(index) ? 'auto' : 0,
                          transition: 'opacity 0.3s, height 0.3s',
                        }}
                      >
                        <div className="accordion-spacer"></div>
                        <p className="color-neutral-100 mg-bottom-0">{item.answer}</p>
                      </div>
                    </div>
                    <div
                      className="btn-square-secondary small accordion-btn"
                      style={{
                        transform: openIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                        borderColor: openIndices.includes(index)
                          ? 'rgb(255, 255, 255)'
                          : 'rgb(36, 123, 254)',
                      }}
                    >
                      <div
                        className="accordion-item-line-icon horizontal"
                        style={{
                          backgroundColor: openIndices.includes(index)
                            ? 'rgb(255, 255, 255)'
                            : 'rgb(36, 123, 254)',
                          transform: openIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      ></div>
                      <div
                        className="accordion-item-line-icon vertical"
                        style={{
                          backgroundColor: openIndices.includes(index)
                            ? 'rgb(255, 255, 255)'
                            : 'rgb(36, 123, 254)',
                          transform: openIndices.includes(index)
                            ? 'rotate(270deg)'
                            : 'rotate(90deg)',
                        }}
                      ></div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                className="text-center mg-top-60px"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                  delay: 0.3,
                }}
                style={{ margin: '48px 0 0' }}
              >
                <Link
                  href="#pricing"
                  className="btn-primary w-button"
                  onClick={handleScrollToPricing}
                >
                  Get Started
                  <span className="line-rounded-icon link-icon-right"></span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <div className="divider _0px bg-neutral-700"></div>
    </div>
  );
};

export default CMCFAQ;
